import moment from 'moment/moment';
import { first, get, isEmpty, merge, reduce, isNull } from 'lodash';
import {
  forecastPeriodConfigNotAvailable,
  forecastPeriodDateFormat,
  forecastPeriodNotAvailable,
} from './teleconnections.constants';
import {
  ForecastPeriodConfig,
  ForecastPeriod,
  ForecastPeriodTeleconnectionModels,
  TrendsDatum,
  Run,
  Forecast,
  EnsembleGroup,
  Ensemble,
  ChartTypes,
} from './teleconnection.types';
import { SeriesOptionsType } from 'highcharts';

const createFormatedForecastPeriodDate = (date: string | null): string =>
  isNull(date)
    ? forecastPeriodNotAvailable
    : moment(date).format(forecastPeriodDateFormat);

const createForecastPeriodDate = (date: string, forecastHour: number): string =>
  moment(date)
    .add(forecastHour, 'hours')
    .utc()
    .utcOffset(Math.abs(moment().utcOffset()))
    .format('YYYY-MM-DDTHH:mm:ss[Z]');

export const createForecastPeriodConfigFormatted = (
  startDate: string | null,
  endDate: string | null
): string =>
  `${createFormatedForecastPeriodDate(
    startDate
  )}-${createFormatedForecastPeriodDate(endDate)}`;

export const getForecastPeriodDates = ({
  startInitTime,
  endInitTime,
  startHour,
  endHour,
}: {
  startInitTime: string;
  endInitTime: string;
  startHour: number;
  endHour: number;
}): Omit<ForecastPeriodConfig, 'formatted'> => ({
  startDate: createForecastPeriodDate(startInitTime, startHour),
  endDate: createForecastPeriodDate(endInitTime, endHour),
});

export const createForecastPeriodConfig = ({
  startDate,
  endDate,
}: Omit<ForecastPeriodConfig, 'formatted'>): ForecastPeriodConfig => ({
  startDate,
  endDate,
  formatted: createForecastPeriodConfigFormatted(startDate, endDate),
});

export const createIndividualForecastPeriodConfig = (
  model: ForecastPeriodTeleconnectionModels,
  ensembleGroups: EnsembleGroup[]
): ForecastPeriodConfig => {
  const { ensembles = [], initTime = '' } = reduce(
    ensembleGroups.filter(({ source }) => source.includes(model)),
    (acc, ensembleGroup) => merge(acc, ensembleGroup),
    {} as EnsembleGroup
  );

  if (isEmpty(ensembles)) {
    return forecastPeriodConfigNotAvailable;
  }

  const [{ hour: startHour = 0 }] = (
    first(ensembles) ?? ({ forecasts: [{}] as Forecast[] } as Ensemble)
  ).forecasts;
  const endHour = reduce(
    ensembles,
    (acc, { forecasts }) => {
      const { hour = 0 } = forecasts.at(-1) ?? ({} as Forecast);

      return Math.max(acc, hour);
    },
    0
  );

  return createForecastPeriodConfig(
    getForecastPeriodDates({
      startInitTime: initTime,
      endInitTime: initTime,
      startHour,
      endHour,
    })
  );
};

export const createModelComparisonForecastPeriodConfig = (
  forecastPeriod: ForecastPeriod
): ForecastPeriodConfig => {
  const individualForecastPeriodConfigs = Object.values(
    forecastPeriod?.individualRefs ?? {}
  );

  return isEmpty(individualForecastPeriodConfigs)
    ? forecastPeriodConfigNotAvailable
    : createForecastPeriodConfig(
        reduce(
          individualForecastPeriodConfigs,
          (acc, { startDate, endDate }) => {
            const { startDate: accStartDate, endDate: accEndDate } = acc;

            if (!accStartDate && !accEndDate) {
              return {
                startDate,
                endDate,
              };
            }

            acc.startDate = moment(startDate).isAfter(accStartDate)
              ? startDate
              : accStartDate;
            acc.endDate = moment(endDate).isAfter(accEndDate)
              ? endDate
              : accEndDate;

            return acc;
          },
          {
            startDate: null,
            endDate: null,
          } as Omit<ForecastPeriodConfig, 'formatted'>
        )
      );
};

export const createTrendsForecastPeriodConfig = (
  model: ForecastPeriodTeleconnectionModels,
  modelType: string | undefined,
  trendsData: TrendsDatum[]
): ForecastPeriodConfig => {
  const { runs = [], source = '' } =
    trendsData.find(({ source }) => source === `${model}_${modelType}`) ??
    ({} as TrendsDatum);

  if (isEmpty(runs)) {
    return forecastPeriodConfigNotAvailable;
  }

  const currentModelRuns = runs.map(
    (
      run
    ): {
      source: string;
      initTime: string;
    } & Omit<Run, 'initTime'> => ({
      ...run,
      source,
      initTime: get(run, 'initTime', ''),
    })
  );
  const { initTime: startInitTime = '', forecasts: startForecasts = [] } =
    currentModelRuns.at(-1) ??
    ({} as { initTime: string } & Pick<Run, 'forecasts'>);
  const { hour: startHour = 0 } = first(startForecasts) ?? ({} as Forecast);
  const endHour = reduce(
    currentModelRuns,
    (accEndHour, { forecasts }) => {
      const { hour = 0 } = forecasts.at(-1) ?? ({} as Forecast);

      return Math.max(accEndHour, hour);
    },
    0
  );
  const { initTime: endInitTime = '' } =
    currentModelRuns.find(({ forecasts }) =>
      forecasts.map(({ hour }) => hour).includes(endHour)
    ) ?? ({} as { initTime: string });

  return createForecastPeriodConfig(
    getForecastPeriodDates({
      startInitTime,
      endInitTime,
      startHour,
      endHour,
    })
  );
};

export const createSeriesVisibilityKey = (
  chartType: ChartTypes,
  model = ''
): string => (model ? `${chartType}-${model}` : chartType);

export const applySeriesVisibility = <T extends SeriesOptionsType>(
  series: T[],
  visibility: Record<string, boolean> = {}
): T[] =>
  series.map((item) =>
    item.name && item.name in visibility
      ? { ...item, visible: visibility[item.name] }
      : item
  );

// the selected run comes as e.g. '2026-10-18 12Z' (UTC)
export const getRunInitTime = (run: string | undefined): number | null => {
  const [, date, hour] = run?.match(/^(\d{4}-\d{2}-\d{2}) (\d{2})Z$/) ?? [];

  return date ? Date.parse(`${date}T${hour}:00:00Z`) : null;
};
